// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo, useState } from 'react';
import { useLocalMessage } from '@Helpers/';
import UploadSingleFile from '@Atoms/UploadSingleFile/UploadSingleFile';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { UseValidateFormReturn } from '@Hooks/useValidateForm';
import { CaseCreateFormTabs } from './CaseCreateFormTabs';
import client from '@Client';
import { OrganisationOutput, ProfileOutput, InputShare, CaseInput } from 'thehive-sdk';
import useOneQuery from '@Hooks/useOneQuery';
import { useGenericQuery } from '@Queries/genericQueries';
import useListQuery from '@Hooks/useListQuery';
import { computeGetSelector, computeListSelector } from '@Organisms/Organisation/selector';
import { computeListSelector as computeProfileListSelector } from '@Organisms/Profile/selector';
import { useCurrentUser } from '@Queries/userQueries';
import { Space } from 'antd';
import { AlertImport } from '@Molecules/AlertImport/AlertImport';
import { CaseCreateFormValues } from './CaseCreateForm';
import { getAvailableLinks } from '@Helpers/caseSharing';

interface CaseCreateMISPImportValues {
    archive: any;
    sharingParameters?: (InputShare & { editable?: boolean })[];
    tasks: CaseInput['tasks'];
    customFields: CaseInput['customFields'];
}

interface CaseCreateMISPImportProps {
    onImport: (formData: FormData) => Promise<any>;
    toggleDrawer: () => void;
    caseMISPImportValidationForm: UseValidateFormReturn;
}

export const CaseCreateMISPImport = ({
    onImport,
    toggleDrawer,
    caseMISPImportValidationForm,
}: CaseCreateMISPImportProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, setFieldsTouched } = caseMISPImportValidationForm;
    const { data: currentUser } = useCurrentUser();
    const [stateLoading, setStateLoading] = useState(false);

    const onFinish = ({ archive, ...values }: CaseCreateMISPImportValues): void => {
        const formData = new FormData();

        if (archive) {
            setStateLoading(true);
            formData.set('file', archive as any);
            formData.set('_json', JSON.stringify(values));
            onImport(formData)
                .then(() => {
                    toggleDrawer?.();
                })
                .finally(() => {
                    setStateLoading(false);
                });
        }
    };

    const { data: sharingProfileList = [] } = useGenericQuery({
        fetchCallback: () => client.OrganisationController.getSharingProfile(),
        key: ['get-organisation-sharing-profile'],
        manual: true,
        fetchOnStart: false,
    });

    const { data: currentOrg } = useOneQuery<OrganisationOutput>({
        controller: client.OrganisationController,
        selector: computeGetSelector({ orgId: currentUser?.organisation || '' }),
        manual: true,
        fetchOnStart: false,
    });

    const { data: orgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: computeListSelector(),
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    const { data: profileList = [] } = useListQuery<ProfileOutput[]>({
        queryParams: {
            selector: computeProfileListSelector(),
        },
        controller: client.ProfileController,
        manual: true,
        fetchOnStart: true,
    });

    const orgProfileList = useMemo(() => profileList.filter((profile) => profile.forOrg), [profileList]);

    const availableLinks = useMemo(
        () => currentOrg?.links && getAvailableLinks(currentOrg.links, orgList),
        [currentOrg?.links, orgList],
    );

    const defaultCaseValues: Partial<CaseCreateFormValues> = {
        sharingParameters:
            availableLinks?.map(({ toOrganisation = '', linkType = '' }) => {
                const sharingProfile = sharingProfileList.find(({ name }) => name === linkType);

                return {
                    organisation: toOrganisation,
                    profile: sharingProfile?.permissionProfile,
                    share: sharingProfile?.autoShare,
                    observableRule: sharingProfile?.observableRule,
                    taskRule: sharingProfile?.taskRule,
                    editable: sharingProfile?.editable,
                };
            }) || [],
    };

    return (
        <THForm<CaseCreateMISPImportValues>
            name="misp-import-form"
            form={form}
            onFinish={(values) => {
                onFinish({ ...(defaultCaseValues || {}), ...values });
                form.resetFields();
                toggleDrawer();
                setFieldsTouched(false);
            }}
            initialValues={defaultCaseValues || {}}
            onValuesChange={() => {
                onFormValuesChange();
                setFieldsTouched(true);
            }}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        loading={stateLoading}
                        onCancel={() => {
                            toggleDrawer();
                            form.resetFields();
                            setFieldsTouched(false);
                        }}
                        disabled={!isValid}
                        footerText={i(`general.confirm`)}
                    />
                }
            >
                <Space direction="vertical" className="file-import__container" size="large">
                    <AlertImport title={i('case.create.import.misp.title')}>
                        <>{i('case.create.import.misp.content')}</>
                    </AlertImport>
                    <div className="file-import__upload">
                        <span className="file-import__upload-title">{i('general.import.upload.label')}</span>
                        <THForm.Item
                            name="archive"
                            rules={[
                                {
                                    required: true,
                                    message: i('attachment.create.required'),
                                },
                            ]}
                        >
                            <UploadSingleFile text={i('general.import.upload.content')} accept=".json" />
                        </THForm.Item>

                        <span>{i('case.create.import.misp.instruction')}</span>
                    </div>
                    <CaseCreateFormTabs
                        form={form}
                        onChange={onFormValuesChange}
                        orgList={orgList}
                        orgProfileList={orgProfileList}
                        currentOrg={currentOrg}
                    />
                </Space>
            </THForm.Layout>
        </THForm>
    );
};
